import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';

import {
  MemoryInspiration,
  MemoryInspirationResponse,
} from '#models/db/memory-inspiration/memory-inspiration.model';
import { ApiRepositoryService } from '#repositories/api-repository/api-repository.service';
import { IntermediaryService, MS_DELAY } from '#services/intermediary/intermediary.service';
import { IMemoryInspirationService } from './memory-inspiration.interface';
import { logger } from '#lib/logger';

@Injectable({
  providedIn: 'root',
})
export class MemoryInspirationService implements IMemoryInspirationService {
  path = 'memory-inspirations';

  getMemoryInspirationLoading$ = new Subject<boolean>();
  memoryInspirationPermissionLoading$ = new Subject<boolean>();
  memoryInspiration$ = new BehaviorSubject<MemoryInspiration[] | null>(null);
  currentMemoryInspiration$ = new BehaviorSubject<MemoryInspiration | null>(null);

  cycledThroughInspirations = new Set<number>();
  inspirationsExhausted$ = new BehaviorSubject<boolean>(false);

  constructor(
    private apiRepository: ApiRepositoryService,
    private intermediaryHandler: IntermediaryService
  ) {}

  getMemoryInspiration(
    nextFn?: (data: MemoryInspiration[] | null) => void,
    silent?: boolean,
    permission?: boolean
  ): void {
    const loading = permission
      ? this.memoryInspirationPermissionLoading$
      : this.getMemoryInspirationLoading$;

    this.intermediaryHandler.intermediary(
      {
        loading: !silent ? loading : undefined,
        subject: this.memoryInspiration$,
        delay: !silent ? MS_DELAY : undefined,
        nextFn,
      },
      async () => {
        const response = await this.apiRepository.get<MemoryInspirationResponse>({
          url: this.path,
        });

        return response.details;
      }
    );
  }

  refreshCurrentInspiration() {
    if (
      !this.memoryInspiration$.value ||
      this.cycledThroughInspirations.size >= this.memoryInspiration$.value.length
    ) {
      this.inspirationsExhausted$.next(true);
      return;
    }

    if (this.inspirationsExhausted$.value) {
      this.inspirationsExhausted$.next(false);
    }

    let index;

    do {
      index = Math.floor(Math.random() * this.memoryInspiration$.value.length);
    } while (this.cycledThroughInspirations.has(index));

    const prompt = this.memoryInspiration$.value[index];

    this.cycledThroughInspirations.add(index);
    this.currentMemoryInspiration$.next(prompt);

    logger.log(
      this.cycledThroughInspirations,
      'MemoryInspirationService.refreshCurrentInspiration -> cycledInspirations'
    );
  }

  resetMemoryInspirations() {
    this.memoryInspiration$.next(null);
    this.currentMemoryInspiration$.next(null);
    this.cycledThroughInspirations.clear();
    this.inspirationsExhausted$.next(false);
    logger.log('complete', 'MemoryInspirationService.resetMemoryInspiration');
  }
}
